import { useApolloClient } from '@apollo/client';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { getApiBaseUrl } from '../../lib/getEnv';
import { PATHS } from '../../paths';

import LoginLayout from './LoginLayout';
import {
  EmailEntryStep,
  ForgotPasswordStep,
  NewPasswordStep,
  OTPVerificationStep,
  PasswordResetSmsOtpStep,
  PasswordStep,
  RegistrationStep,
  WaitlistedStep,
} from './steps';
import { LoginStep } from './types';

import type { ILoginFlowState } from './types';
import type { CheckEmailResponse, ILoginResponse } from '../../hooks/useAuthApi';

const INITIAL_STATE: ILoginFlowState = {
  email: '',
  userId: null,
  otpPurpose: 'login',
};

const LoginFlow = () => {
  const navigate = useNavigate();
  const client = useApolloClient();

  const [currentStep, setCurrentStep] = React.useState<LoginStep>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'waitlisted') {
      window.history.replaceState({}, '', window.location.pathname);
      return LoginStep.Waitlisted;
    }
    return LoginStep.EmailEntry;
  });
  const [flowState, setFlowState] = React.useState<ILoginFlowState>(INITIAL_STATE);
  const [passwordSuccessMessage, setPasswordSuccessMessage] = React.useState<string | undefined>();

  const handleAuthenticated = React.useCallback(() => {
    void client.resetStore().then(() => {
      void navigate(PATHS.HOME, { replace: true });
    });
  }, [client, navigate]);

  // ─── Email Entry handlers ──────────────────────────────────────────────

  const handleEmailResult = React.useCallback((result: { email: string; checkResult: CheckEmailResponse }) => {
    const { email, checkResult } = result;

    if ('provider' in checkResult && checkResult.provider === 'GOOGLE') {
      const loginHint = encodeURIComponent(checkResult.email);
      window.location.href = `${getApiBaseUrl()}/auth/google?login_hint=${loginHint}`;
      return;
    }

    setFlowState((prev) => ({ ...prev, email }));

    if ('status' in checkResult) {
      switch (checkResult.status) {
        case 'AVAILABLE':
          setCurrentStep(LoginStep.Registration);
          break;
        case 'EXISTS':
          setCurrentStep(LoginStep.Password);
          break;
        case 'PENDING_VERIFICATION':
          // Route through Registration — register() handles PENDING_VERIFICATION
          // users by returning userId and sending a new verification OTP
          setCurrentStep(LoginStep.Registration);
          break;
        default:
          break;
      }
    }
  }, []);

  // ─── Registration handler ──────────────────────────────────────────────

  const handleRegistered = React.useCallback((result: { userId: string }) => {
    setFlowState((prev) => ({ ...prev, userId: result.userId, otpPurpose: 'registration' }));
    setCurrentStep(LoginStep.OtpVerification);
  }, []);

  // ─── Password (login) handler ──────────────────────────────────────────

  const handleLoginResult = React.useCallback(
    (result: ILoginResponse) => {
      if (!result.requiresOtp) {
        handleAuthenticated();
        return;
      }
      setFlowState((prev) => ({
        ...prev,
        userId: result.userId,
        otpPurpose: 'login',
        otpChannel: result.otpChannel ?? 'email',
        phoneLast4: result.phoneLast4,
      }));
      setCurrentStep(LoginStep.OtpVerification);
    },
    [handleAuthenticated]
  );

  // ─── Forgot password handlers ──────────────────────────────────────────

  const handleForgotPassword = React.useCallback(() => {
    setCurrentStep(LoginStep.ForgotPassword);
  }, []);

  const handleForgotPasswordSent = React.useCallback((result: { requiresSmsOtp: boolean; phoneLast4?: string }) => {
    setFlowState((prev) => ({
      ...prev,
      otpPurpose: 'passwordReset',
      requiresSmsOtp: result.requiresSmsOtp,
      phoneLast4: result.phoneLast4,
    }));
    setCurrentStep(LoginStep.NewPassword);
  }, []);

  const handleSmsOtpRequired = React.useCallback((data: { emailCode: string; newPassword: string }) => {
    setFlowState((prev) => ({
      ...prev,
      pendingEmailCode: data.emailCode,
      pendingNewPassword: data.newPassword,
    }));
    setCurrentStep(LoginStep.PasswordResetSmsOtp);
  }, []);

  const handlePasswordReset = React.useCallback(() => {
    setPasswordSuccessMessage('Password reset successfully. Please sign in.');
    setCurrentStep(LoginStep.Password);
  }, []);

  // ─── Waitlist handler ────────────────────────────────────────────────

  const handleWaitlisted = React.useCallback(() => {
    setCurrentStep(LoginStep.Waitlisted);
  }, []);

  // ─── Back navigation ──────────────────────────────────────────────────

  const handleBackToEmail = React.useCallback(() => {
    setFlowState(INITIAL_STATE);
    setPasswordSuccessMessage(undefined);
    setCurrentStep(LoginStep.EmailEntry);
  }, []);

  const handleBackFromOtp = React.useCallback(() => {
    if (flowState.otpPurpose === 'registration') {
      setCurrentStep(LoginStep.Registration);
    } else {
      setCurrentStep(LoginStep.Password);
    }
  }, [flowState.otpPurpose]);

  const handleBackToPassword = React.useCallback(() => {
    setCurrentStep(LoginStep.Password);
  }, []);

  const handleBackToForgotPassword = React.useCallback(() => {
    setCurrentStep(LoginStep.ForgotPassword);
  }, []);

  const handleBackToNewPassword = React.useCallback(() => {
    setCurrentStep(LoginStep.NewPassword);
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (currentStep) {
      case LoginStep.EmailEntry:
        return <EmailEntryStep onNext={handleEmailResult} />;

      case LoginStep.Registration:
        return <RegistrationStep email={flowState.email} onNext={handleRegistered} onBack={handleBackToEmail} />;

      case LoginStep.Password:
        return (
          <PasswordStep
            email={flowState.email}
            successMessage={passwordSuccessMessage}
            onNext={handleLoginResult}
            onForgotPassword={handleForgotPassword}
            onWaitlisted={handleWaitlisted}
            onBack={handleBackToEmail}
          />
        );

      case LoginStep.OtpVerification:
        return (
          <OTPVerificationStep
            email={flowState.email}
            userId={flowState.userId ?? ''}
            otpPurpose={flowState.otpPurpose}
            otpChannel={flowState.otpChannel}
            phoneLast4={flowState.phoneLast4}
            onSuccess={handleAuthenticated}
            onWaitlisted={handleWaitlisted}
            onBack={handleBackFromOtp}
          />
        );

      case LoginStep.ForgotPassword:
        return (
          <ForgotPasswordStep email={flowState.email} onNext={handleForgotPasswordSent} onBack={handleBackToPassword} />
        );

      case LoginStep.NewPassword:
        return (
          <NewPasswordStep
            email={flowState.email}
            requiresSmsOtp={flowState.requiresSmsOtp}
            onSuccess={handlePasswordReset}
            onSmsOtpRequired={handleSmsOtpRequired}
            onBack={handleBackToForgotPassword}
          />
        );

      case LoginStep.PasswordResetSmsOtp:
        return (
          <PasswordResetSmsOtpStep
            email={flowState.email}
            emailCode={flowState.pendingEmailCode ?? ''}
            newPassword={flowState.pendingNewPassword ?? ''}
            phoneLast4={flowState.phoneLast4}
            onSuccess={handlePasswordReset}
            onBack={handleBackToNewPassword}
          />
        );

      case LoginStep.Waitlisted:
        return <WaitlistedStep onBack={handleBackToEmail} />;

      default:
        return null;
    }
  };

  return <LoginLayout>{renderStep()}</LoginLayout>;
};

export default LoginFlow;
